import { useEffect, useMemo } from 'react';
import { useMap } from 'react-leaflet';
import { useSelector } from 'react-redux';
import L from 'leaflet';
import {
  processTrafficSegments,
  getSegmentColor,
  getTrafficDescription,
  calculateDelayText,
} from '../../utils/trafficHelpers';

const TRAFFIC_PANE = 'trafficPane';
const TRAFFIC_PANE_Z_INDEX = 450; // above overlayPane (400), below shadowPane (500) and markerPane (600)

const TrafficRouteSegments = ({ trafficMatrix, routingRoutes, tourData, selectedTour }) => {
  const map = useMap();
  const trafficTimeOffset = useSelector((state) => state.user.trafficTimeOffset || 0);

  // Ensure the custom pane exists (idempotent — Leaflet throws if pane already exists)
  useEffect(() => {
    if (!map) return;
    if (!map.getPane(TRAFFIC_PANE)) {
      map.createPane(TRAFFIC_PANE).style.zIndex = TRAFFIC_PANE_Z_INDEX;
    }
  }, [map]);

  const { segmentData, minCongestion, maxCongestion } = useMemo(() => {
    return processTrafficSegments({
      trafficMatrix,
      tourData,
      selectedTour,
      trafficTimeOffset,
    });
  }, [trafficMatrix, tourData, selectedTour, trafficTimeOffset]);

  useEffect(() => {
    if (!map || segmentData.length === 0) {
      return;
    }

    const overlayLines = [];
    let routeFeatures = [];

    if (routingRoutes?.features && Array.isArray(routingRoutes.features)) {
      const tourRouteId = selectedTour !== undefined ? selectedTour : 0;

      routeFeatures = routingRoutes.features.filter(
        (feature) =>
          feature.geometry?.type === 'LineString' &&
          feature.properties?.isRouting === true &&
          feature.properties?.routeId === tourRouteId &&
          feature.properties?.fromStop !== undefined,
      );
    }

    segmentData.forEach((segment) => {
      const { segmentIndex, congestion, offsetDepartureTime, from, to } = segment;
      const color = getSegmentColor(congestion);
      const description = getTrafficDescription(congestion);

      // Prefer detailed routing route geometry; fall back to straight line between origins
      let coordinates;
      const routeFeature =
        routeFeatures.length > 0
          ? routeFeatures.find((feature) => feature.properties?.fromStop === segmentIndex)
          : null;

      if (routeFeature?.geometry?.coordinates) {
        coordinates = routeFeature.geometry.coordinates.map((coord) => [coord[1], coord[0]]);
      } else {
        coordinates = [from, to];
      }

      const polyline = L.polyline(coordinates, {
        color,
        weight: 8,
        opacity: 0.9,
        smoothFactor: 1,
        lineCap: 'round',
        lineJoin: 'round',
        className: 'traffic-overlay',
        pane: TRAFFIC_PANE,
      });

      const timeStr = new Date(offsetDepartureTime).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      const delayText = calculateDelayText(congestion);

      const tooltipContent = `
          <div style="font-size: 13px; padding: 6px; background: white; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
            <strong>Time: ${timeStr}</strong><br/>
            <strong>Factor: ${congestion.toFixed(3)}</strong><br/>
            <span style="color: ${color};">●</span> ${description}<br/>
            <strong>${delayText}</strong><br/>
            <small>Tour range: ${minCongestion.toFixed(3)} - ${maxCongestion.toFixed(3)}</small>
          </div>
        `;

      polyline.bindTooltip(tooltipContent, { sticky: true });
      polyline.addTo(map);
      overlayLines.push(polyline);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      overlayLines.forEach((line) => map.removeLayer(line));
    };
  }, [map, segmentData, routingRoutes, selectedTour, minCongestion, maxCongestion]);

  return null;
};

export default TrafficRouteSegments;
